import React, { useMemo, useState } from 'react';
import { useSectionStore } from '../../store/sectionStore';
import { useGradePaletteStore } from '../../store/gradePaletteStore';
import { getSite } from '../../config/siteConfig';
import { downloadSectionPlot, renderSectionPlotCanvas } from '../../Utils/sectionPlotExport';
import { holesOnSection } from '../../Utils/sectionPlot';
import { DEFAULT_DENSITY, reportBlocksInClip } from '../../Utils/blockModelReport';
import { fmtGrade } from '../../Utils/gradePalette';

export default function SectionPlotModal({ open, onClose }) {
  const sectionKey = useSectionStore(
    (s) =>
      `${s.enabled}|${s.orientation}|${s.centerEast}|${s.centerNorth}|${s.centerRl}|${s.width}|${s.azimuth}|${s.clipMode}`
  );
  const thresholds = useGradePaletteStore((s) => s.thresholds);
  const [intervalMode, setIntervalMode] = useState('assay');
  const [density, setDensity] = useState(DEFAULT_DENSITY);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const holePack = useMemo(() => {
    if (!open) return { holes: [], basis: null };
    void sectionKey;
    return holesOnSection(useSectionStore.getState(), { thresholds, intervalMode });
  }, [open, intervalMode, thresholds, sectionKey]);

  const report = useMemo(() => {
    if (!open) return null;
    void sectionKey;
    return reportBlocksInClip({ density, thresholds, sectionState: useSectionStore.getState() });
  }, [open, density, thresholds, sectionKey]);

  const previewUrl = useMemo(() => {
    if (!open) return '';
    void sectionKey;
    try {
      const canvas = renderSectionPlotCanvas(useSectionStore.getState(), {
        projectName: getSite().projectName,
        thresholds,
        intervalMode,
        width: 1100,
        height: 700,
      });
      return canvas.toDataURL('image/png');
    } catch {
      return '';
    }
  }, [open, intervalMode, thresholds, sectionKey]);

  if (!open) return null;

  const projectName = getSite().projectName;
  const runExport = async (format) => {
    setError('');
    setBusy(format);
    try {
      await downloadSectionPlot(useSectionStore.getState(), {
        projectName,
        format,
        thresholds,
        intervalMode,
      });
    } catch (e) {
      setError(e?.message || 'Export failed');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="fixed inset-0 z-[170] bg-black/75 flex items-center justify-center p-3 backdrop-blur-[2px]">
      <div
        role="dialog"
        aria-label="Section plot"
        className="w-full max-w-5xl max-h-[min(96dvh,52rem)] overflow-hidden rounded-md border border-yellow-600/40 bg-zinc-950 shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col"
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-zinc-800">
          <div>
            <p className="text-[9px] uppercase tracking-[0.16em] text-yellow-500/80">Section plot</p>
            <h3 className="text-sm font-semibold text-yellow-400 tracking-wide">Holes on plane</h3>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              {holePack.holes.length} hole{holePack.holes.length === 1 ? '' : 's'} in the corridor
              {report?.cells ? ` · ${report.cells.toLocaleString()} blocks` : ''}
            </p>
          </div>
          <button
            type="button"
            className="m-0 h-7 px-2 text-[10px] uppercase tracking-wider text-zinc-400 border border-zinc-700 rounded hover:border-yellow-500/50 hover:text-yellow-400"
            onClick={onClose}
          >
            Close
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto grid md:grid-cols-[1fr_16rem] gap-0">
          <div className="bg-black p-2 min-h-[16rem]">
            {previewUrl ? (
              <img src={previewUrl} alt="Section plot preview" className="w-full h-auto border border-zinc-800" />
            ) : (
              <p className="text-[11px] text-zinc-500 p-4">Turn a section on and load holes to plot traces.</p>
            )}
          </div>
          <div className="border-t md:border-t-0 md:border-l border-zinc-800 p-3 space-y-3 overflow-y-auto">
            <div className="inline-flex rounded-sm border border-yellow-600/30 overflow-hidden">
              <button
                type="button"
                className={`m-0 px-2 py-1 text-[9px] font-semibold uppercase tracking-wider ${
                  intervalMode === 'assay' ? 'bg-yellow-500/20 text-yellow-300' : 'text-zinc-500'
                }`}
                onClick={() => setIntervalMode('assay')}
              >
                Assay
              </button>
              <button
                type="button"
                className={`m-0 px-2 py-1 text-[9px] font-semibold uppercase tracking-wider ${
                  intervalMode === 'lith' ? 'bg-emerald-500/20 text-emerald-300' : 'text-zinc-500'
                }`}
                onClick={() => setIntervalMode('lith')}
              >
                Lith
              </button>
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                disabled={!!busy}
                className="m-0 h-8 flex-1 rounded-sm border border-yellow-600/40 bg-yellow-500 text-[10px] font-semibold uppercase tracking-wider text-black hover:bg-yellow-400 disabled:opacity-50"
                onClick={() => runExport('png')}
              >
                {busy === 'png' ? '…' : 'PNG'}
              </button>
              <button
                type="button"
                disabled={!!busy}
                className="m-0 h-8 flex-1 rounded-sm border border-yellow-600/40 text-[10px] font-semibold uppercase tracking-wider text-yellow-400 hover:bg-yellow-500/10 disabled:opacity-50"
                onClick={() => runExport('pdf')}
              >
                {busy === 'pdf' ? '…' : 'PDF'}
              </button>
            </div>
            {error ? <p className="text-[10px] text-red-400">{error}</p> : null}
            <div>
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">Holes on section</p>
              <div className="max-h-36 overflow-y-auto space-y-0.5">
                {holePack.holes.length === 0 ? (
                  <p className="text-[10px] text-zinc-500">None in this corridor.</p>
                ) : (
                  holePack.holes.map((h) => (
                    <p key={`${h.dataset}::${h.holeId}`} className="text-[10px] text-zinc-300 truncate">
                      {h.holeId}
                    </p>
                  ))
                )}
              </div>
            </div>
            <label className="block text-[9px] uppercase tracking-wider text-zinc-500">
              Density t/m³
              <input
                type="number"
                min="0.5"
                step="0.05"
                value={density}
                onChange={(e) => setDensity(Number(e.target.value) || DEFAULT_DENSITY)}
                className="mt-0.5 m-0 h-7 w-full rounded-sm border border-yellow-600/30 bg-zinc-950 px-1.5 text-[10px] text-yellow-100"
              />
            </label>
            {report ? (
              <div>
                <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
                  Blocks {report.clipped ? 'in clip' : '(all loaded)'}
                </p>
                {report.orientation === 'object' ? (
                  <p className="text-[10px] text-zinc-500">Object clip is not used for tonnes yet — switch to corridor or box.</p>
                ) : (
                  <>
                    <p className="text-[11px] text-yellow-100 tabular-nums">
                      {report.tonnes.toLocaleString(undefined, { maximumFractionDigits: 0 })} t @ {fmtGrade(report.meanGrade)} g/t
                    </p>
                    <p className="text-[10px] text-zinc-500 tabular-nums">
                      {report.cells.toLocaleString()} cells · {fmtGrade(report.ounces)} oz
                    </p>
                    <div className="mt-1 space-y-0.5">
                      {report.bins.filter((b) => b.cells).map((b) => (
                        <div key={b.label} className="flex items-center justify-between gap-2 text-[10px]">
                          <span className="flex items-center gap-1 min-w-0">
                            <span className="inline-block w-2 h-2 rounded-sm shrink-0" style={{ background: b.css }} />
                            <span className="text-zinc-400 truncate">{b.label}</span>
                          </span>
                          <span className="tabular-nums text-zinc-200 shrink-0">
                            {b.tonnes.toLocaleString(undefined, { maximumFractionDigits: 0 })} t
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
